import Vue from 'vue'
import { Module, GetterTree, MutationTree, ActionTree } from 'vuex'
import axios from 'axios'
import moment from 'moment'
import Modernizr from 'modernizr'
import {
  Field,
  FieldSpec,
  FieldValue,
  FieldDependency,
  Selected,
  Checked,
  Greater,
  loadSpecs,
  loadFields,
  initField,
  requirementPasses,
} from '@/model/Field'
import { API_URL } from '@/config'
import * as fieldsConfig from '../../service-form-requirements.yml'

export const SET_SERVICE_ATTACHMENT = 'SET_SERVICE_ATTACHMENT'
export const SERVICE_NAMESPACE = 'serviceForm'
const specs = loadSpecs(fieldsConfig)

export class FormState {
  fields: Field[] = loadFields(fieldsConfig)
  attachments: File[] = []
}

const getters: GetterTree<FormState, any> = {
  field: state => (fieldName: string): Field | undefined => {
    return state.fields.find(field => field.name === fieldName)
  },
  show: (state, getters) => (fieldName: string): boolean => {
    let spec = specs.find(spec => spec.field === fieldName)
    if (!spec) {
      return true
    }

    if (spec.when === 'always') {
      return true
    } else {
      let checkDepencency = (dependency: FieldDependency) => {
        let dependencyField: Field | undefined = getters.field(dependency.field)
        if (!dependencyField || dependencyField.value === undefined) {
          return false
        }

        // Don't show field if a field it depends on is hidden.
        if (!getters.show(dependencyField.name)) {
          return false
        }

        return requirementPasses(dependency.requirement, dependencyField.value)
      }

      if (spec.when.type === 'either') {
        return spec.when.dependencies.some(checkDepencency)
      } else {
        return spec.when.dependencies.every(checkDepencency)
      }
    }
  },
  fieldValue: (state, getters) => (fieldName: string): FieldValue => {
    let field: Field | undefined = getters.field(fieldName)
    if (field) {
      return field.value
    } else {
      return undefined
    }
  },
  fieldValueMap: (state, getters): Map<string, FieldValue> => {
    let map = new Map()
    for (let field of state.fields) {
      if (getters.show(field.name)) {
        map.set(field.name, field.value)
      }
    }
    return map
  },
  inquiry: (state, getters): Object => {
    let map = getters.fieldValueMap

    function format_date (date?: string): string | undefined {
      if (date) {
        return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
      } else {
        return undefined
      }
    }

    return {
      from: map.get('from'),
      order_number: parseInt(map.get('order_no'), 10),
      year_delivered: parseInt(map.get('year_delivered'), 10),
      description: map.get('description'),
      material: map.get('material'),
      form_type: map.get('form_type'),
      person: (() => ({
        name: map.get('name'),
        address: map.get('address'),
        postal_number: map.get('postal_num'),
        postal_place: map.get('postal_place'),
        floor: parseInt(map.get('floor'), 10),
        elevator: map.get('elevator') || false,
        email: (() => {
          const email = map.get('email');
          if (email !== undefined && email.length > 0) {
            return email;
          } else {
            return undefined;
          }
        })(),
        phone: map.get('phone'),
      }))(),
      retailer: (() => {
        const from_retailer = (map.get('from') == 'retailer');
        if (!from_retailer) {
          return undefined;
        }

        return {
          retailer: map.get('retailer_name'),
          name: map.get('retailer_person'),
          phone: map.get('retailer_phone'),
          email: map.get('retailer_email'),
        };
      })(),
    }
  }
}

const actions: ActionTree<FormState, any> = {
  sendInquiry: ({ state, getters, rootState }, testmail: boolean): Promise<string> => {
    const data = new FormData()

    const inquiry = getters.inquiry;
    if (testmail) {
      inquiry.testmail = true;
    }
    data.append('inquiry', JSON.stringify(inquiry))

    for (let i = 0; i < state.attachments.length; ++i) {
      const attachment = state.attachments[i];
      data.append(`attachments`, attachment);
    }

    return axios.post(`${API_URL}/service_inquiry`, data)
    .then(response => {
      return Promise.resolve(response.data.id)
    }).catch(error => {
      return Promise.reject(error)
    })
  },
}

const mutations: MutationTree<FormState> = {
  setFieldValue(state, { fieldName, value }) {
    let fieldIndex = state.fields.findIndex(field => field.name === fieldName)
    if (fieldIndex === -1) {
      state.fields.push(initField(fieldName))
      fieldIndex = state.fields.length - 1
    }

    Vue.set(state.fields, fieldIndex, {
      ...state.fields[fieldIndex],
      value,
    })

    if (Modernizr.localstorage && state.fields[fieldIndex].store) {
      window.localStorage.setItem(fieldName, value)
    }
  },
  setAttachment(state, { index, file }) {
    if (index < state.attachments.length) {
      state.attachments[index] = file;
    } else {
      state.attachments.push(file);
    }
  }
}

let form: Module<FormState, any> = {
  namespaced: true,
  state: new FormState(),
  getters,
  actions,
  mutations,
}

export default form
